import React, { useState, useEffect } from "react";
import { useApi } from "../../../hooks/useApi";
import api from "../../../utils/api";
import "./ThemeOptions.scss";

const ThemeOptions = () => {
  const { data, loading, refetch } = useApi(() => api.getAdminThemeOptions(), []);
  const [activeTab, setActiveTab] = useState("general");
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [uploading, setUploading] = useState({});

  useEffect(() => {
    if (data?.themeOptions) {
      setFormData(data.themeOptions);
    }
  }, [data]);

  const handleChange = (path, value) => {
    const keys = path.split(".");
    const newData = { ...formData };
    let current = newData;

    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }

    current[keys[keys.length - 1]] = value;
    setFormData(newData);
    setMessage({ type: "", text: "" });
  };

  const handleArrayChange = (path, index, field, value) => {
    const keys = path.split(".");
    const newData = { ...formData };
    let current = newData;

    for (const key of keys) {
      if (!current[key]) {
        current[key] = [];
      }
      current = current[key];
    }

    if (!current[index]) {
      current[index] = {};
    }
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      if (!current[index][parent]) {
        current[index][parent] = {};
      }
      current[index][parent][child] = value;
    } else {
      current[index][field] = value;
    }

    setFormData(newData);
  };

  const addArrayItem = (path) => {
    const keys = path.split(".");
    const newData = { ...formData };
    let current = newData;

    for (const key of keys) {
      if (!current[key]) {
        current[key] = [];
      }
      current = current[key];
    }

    current.push({});
    setFormData(newData);
  };

  const removeArrayItem = (path, index) => {
    const keys = path.split(".");
    const newData = { ...formData };
    let current = newData;

    for (const key of keys) {
      current = current[key];
    }

    current.splice(index, 1);
    setFormData(newData);
  };

  const handleFileUpload = async (file, path) => {
    if (!file) return;

    // Extract a simple key from the path (e.g., "banner1" from "homePageBanners.banner1.mediaItemUrl")
    const pathParts = path.split('.');
    const uploadKey = pathParts[pathParts.length - 2] || pathParts[pathParts.length - 1];
    
    setUploading(prev => ({ ...prev, [uploadKey]: true }));
    setMessage({ type: "", text: "" });

    try {
      const response = await api.uploadFile(file);
      const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:8000';
      const fullUrl = `${baseUrl}${response.url}`;
      handleChange(path, fullUrl);
      setMessage({ type: "success", text: "File uploaded successfully!" });
    } catch (error) {
      console.error("Error uploading file:", error);
      setMessage({ type: "error", text: error.message || "Failed to upload file" });
    } finally {
      setUploading(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      await api.updateThemeOptions(formData);
      setMessage({ type: "success", text: "Theme options saved successfully!" });
      refetch();
    } catch (error) {
      console.error("Error saving theme options:", error);
      setMessage({ type: "error", text: error.message || "Failed to save theme options" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading theme options...</div>;
  }

  const tabs = [
    { id: "general", label: "General" },
    { id: "banners", label: "Home Banners" },
    { id: "slider", label: "Home Slider" },
    { id: "info", label: "Home Info" },
  ];

  return (
    <div className="admin-theme-options">
      <div className="theme-options-header">
        <h2>Theme Options</h2>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>{message.text}</div>
      )}

      <div className="theme-options-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={activeTab === tab.id ? "active" : ""}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="theme-options-form">
        {/* General Tab */}
        {activeTab === "general" && (
          <div className="tab-content">
            <h3>General Settings</h3>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={formData.general?.email || ""}
                onChange={(e) => handleChange("general.email", e.target.value)}
                placeholder="info@begmart.com"
              />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input
                type="text"
                value={formData.general?.phone || ""}
                onChange={(e) => handleChange("general.phone", e.target.value)}
                placeholder="+355 123 456 789"
              />
            </div>
            <div className="form-group">
              <label>Payment Methods Image URL</label>
              <input
                type="url"
                value={formData.general?.paymentMethodImage?.mediaItemUrl || ""}
                onChange={(e) =>
                  handleChange("general.paymentMethodImage.mediaItemUrl", e.target.value)
                }
                placeholder="https://example.com/payment-methods.jpg"
              />
              {formData.general?.paymentMethodImage?.mediaItemUrl && (
                <div className="image-preview">
                  <img
                    src={formData.general.paymentMethodImage.mediaItemUrl}
                    alt="Payment methods"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Banners Tab */}
        {activeTab === "banners" && (
          <div className="tab-content">
            <h3>Home Page Banners</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Banner 1 Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handleFileUpload(file, "homePageBanners.banner1.mediaItemUrl");
                    }
                  }}
                  disabled={uploading.banner1}
                />
                {uploading.banner1 && <div className="upload-status">Uploading...</div>}
                {formData.homePageBanners?.banner1?.mediaItemUrl && (
                  <div className="image-preview">
                    <img
                      src={formData.homePageBanners.banner1.mediaItemUrl}
                      alt="Banner 1"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => handleChange("homePageBanners.banner1.mediaItemUrl", "")}
                      className="btn-remove-image"
                    >
                      Remove
                    </button>
                  </div>
                )}
                <small className="form-hint">Upload an image file (max 5MB)</small>
              </div>
              <div className="form-group">
                <label>Banner 1 Link</label>
                <input
                  type="text"
                  value={formData.homePageBanners?.banner1Link || ""}
                  onChange={(e) =>
                    handleChange("homePageBanners.banner1Link", e.target.value)
                  }
                  placeholder="/kategoria/featured"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Banner 2 Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handleFileUpload(file, "homePageBanners.banner2.mediaItemUrl");
                    }
                  }}
                  disabled={uploading.banner2}
                />
                {uploading.banner2 && <div className="upload-status">Uploading...</div>}
                {formData.homePageBanners?.banner2?.mediaItemUrl && (
                  <div className="image-preview">
                    <img
                      src={formData.homePageBanners.banner2.mediaItemUrl}
                      alt="Banner 2"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => handleChange("homePageBanners.banner2.mediaItemUrl", "")}
                      className="btn-remove-image"
                    >
                      Remove
                    </button>
                  </div>
                )}
                <small className="form-hint">Upload an image file (max 5MB)</small>
              </div>
              <div className="form-group">
                <label>Banner 2 Link</label>
                <input
                  type="text"
                  value={formData.homePageBanners?.banner2Link || ""}
                  onChange={(e) =>
                    handleChange("homePageBanners.banner2Link", e.target.value)
                  }
                  placeholder="/kategoria/recommended"
                />
              </div>
            </div>
          </div>
        )}

        {/* Slider Tab */}
        {activeTab === "slider" && (
          <div className="tab-content">
            <h3>Home Page Slider</h3>
            <div className="form-group">
              <label>Logo Image URL</label>
              <input
                type="url"
                value={formData.homePageSlider?.logo?.mediaItemUrl || ""}
                onChange={(e) =>
                  handleChange("homePageSlider.logo.mediaItemUrl", e.target.value)
                }
                placeholder="https://example.com/logo.jpg"
              />
              {formData.homePageSlider?.logo?.mediaItemUrl && (
                <div className="image-preview">
                  <img
                    src={formData.homePageSlider.logo.mediaItemUrl}
                    alt="Logo"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Slider Items</label>
              {(formData.homePageSlider?.sliderItems || []).map((item, index) => (
                <div key={index} className="array-item">
                  <div className="array-item-header">
                    <h4>Slider Item {index + 1}</h4>
                    <button
                      type="button"
                      onClick={() => removeArrayItem("homePageSlider.sliderItems", index)}
                      className="btn-remove"
                    >
                      Remove
                    </button>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Title</label>
                      <input
                        type="text"
                        value={item.title || ""}
                        onChange={(e) =>
                          handleArrayChange("homePageSlider.sliderItems", index, "title", e.target.value)
                        }
                        placeholder="Welcome to Begmart"
                      />
                    </div>
                    <div className="form-group">
                      <label>Subtitle</label>
                      <input
                        type="text"
                        value={item.subtitle || ""}
                        onChange={(e) =>
                          handleArrayChange("homePageSlider.sliderItems", index, "subtitle", e.target.value)
                        }
                        placeholder="Your trusted online store"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Desktop Image URL</label>
                      <input
                        type="url"
                        value={item.image?.mediaItemUrl || ""}
                        onChange={(e) =>
                          handleArrayChange("homePageSlider.sliderItems", index, "image.mediaItemUrl", e.target.value)
                        }
                        placeholder="https://example.com/slider-desktop.jpg"
                      />
                    </div>
                    <div className="form-group">
                      <label>Mobile Image URL</label>
                      <input
                        type="url"
                        value={item.mobileImage?.mediaItemUrl || ""}
                        onChange={(e) =>
                          handleArrayChange("homePageSlider.sliderItems", index, "mobileImage.mediaItemUrl", e.target.value)
                        }
                        placeholder="https://example.com/slider-mobile.jpg"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Link URL</label>
                      <input
                        type="text"
                        value={item.link?.url || ""}
                        onChange={(e) =>
                          handleArrayChange("homePageSlider.sliderItems", index, "link.url", e.target.value)
                        }
                        placeholder="/"
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={item.link?.openInNewTab || false}
                          onChange={(e) =>
                            handleArrayChange("homePageSlider.sliderItems", index, "link.openInNewTab", e.target.checked)
                          }
                        />
                        Open in new tab
                      </label>
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem("homePageSlider.sliderItems")}
                className="btn-add"
              >
                Add Slider Item
              </button>
            </div>
          </div>
        )}

        {/* Info Tab */}
        {activeTab === "info" && (
          <div className="tab-content">
            <h3>Home Page Info Items</h3>
            {(formData.homePageInfo?.info || []).map((item, index) => (
              <div key={index} className="array-item">
                <div className="array-item-header">
                  <h4>Info Item {index + 1}</h4>
                  <button
                    type="button"
                    onClick={() => removeArrayItem("homePageInfo.info", index)}
                    className="btn-remove"
                  >
                    Remove
                  </button>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Title</label>
                    <input
                      type="text"
                      value={item.title || ""}
                      onChange={(e) =>
                        handleArrayChange("homePageInfo.info", index, "title", e.target.value)
                      }
                      placeholder="Fast Delivery"
                    />
                  </div>
                  <div className="form-group">
                    <label>Subtitle</label>
                    <input
                      type="text"
                      value={item.subtitle || ""}
                      onChange={(e) =>
                        handleArrayChange("homePageInfo.info", index, "subtitle", e.target.value)
                      }
                      placeholder="Free shipping on orders over $50"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Link</label>
                    <input
                      type="text"
                      value={item.link || ""}
                      onChange={(e) =>
                        handleArrayChange("homePageInfo.info", index, "link", e.target.value)
                      }
                      placeholder="/informacione/delivery"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Desktop Photo URL</label>
                    <input
                      type="url"
                      value={item.photo?.mediaItemUrl || ""}
                      onChange={(e) =>
                        handleArrayChange("homePageInfo.info", index, "photo.mediaItemUrl", e.target.value)
                      }
                      placeholder="https://example.com/photo-desktop.jpg"
                    />
                  </div>
                  <div className="form-group">
                    <label>Mobile Photo URL</label>
                    <input
                      type="url"
                      value={item.mobilePhoto?.mediaItemUrl || ""}
                      onChange={(e) =>
                        handleArrayChange("homePageInfo.info", index, "mobilePhoto.mediaItemUrl", e.target.value)
                      }
                      placeholder="https://example.com/photo-mobile.jpg"
                    />
                  </div>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => addArrayItem("homePageInfo.info")}
              className="btn-add"
            >
              Add Info Item
            </button>
          </div>
        )}

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? "Saving..." : "Save Theme Options"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ThemeOptions;

